import { z } from "zod";
import type { UserRef } from "./api-types";

/**
 * Receipts, bills, and expenses (spec §10): a photo or PDF, read by AI into vendor, amount, dates,
 * bill vs expense, and category; reviewed and marked Ready by the person who submitted it (or the
 * office); approved by someone with approve permission. Amounts in cents.
 */

export const RECEIPT_STATUSES = ["reading", "read_failed", "draft", "ready", "approved"] as const;
export type ReceiptStatus = (typeof RECEIPT_STATUSES)[number];

export const RECEIPT_STATUS_LABELS: Record<ReceiptStatus, string> = {
  reading: "Reading",
  read_failed: "Couldn't read",
  draft: "Needs review",
  ready: "Ready",
  approved: "Approved",
};

/**
 * A bill is owed later (a supplier or sub invoice with a due date); an expense is already paid; a
 * credit is a vendor credit (a credit memo, a return) or a card refund. A credit's total is positive
 * and its lines carry the minus.
 */
export type ReceiptKind = "bill" | "expense" | "credit";

export const PAYMENT_METHODS = ["Company card", "Cash", "Check", "Personal (reimburse)", "Other"] as const;

/** Sales tax is a line of its own, on a job like any other line. */
export const SALES_TAX = "Sales tax";

/** The categories a line can have: Settings → Lists' expense categories, plus Sales tax. */
export function lineCategories(expenseCategories: readonly string[]): string[] {
  return expenseCategories.some((c) => c.toLowerCase() === SALES_TAX.toLowerCase()) ? [...expenseCategories] : [...expenseCategories, SALES_TAX];
}

/** One line of a receipt or bill (like QuickBooks' category details): what, how much, and which job. */
export interface ReceiptLine {
  category: string;
  description: string;
  amountCents: number;
  jobId: string | null;
}

/** What the AI reads off the receipt, as it says it (dollars). */
export interface ReceiptReading {
  vendorName: string;
  invoiceNumber: string;
  date: string | null;
  dueDate: string | null;
  kind: ReceiptKind;
  category: string;
  paymentMethod: string;
  /** The last 4 digits of the card used, when the receipt prints them. */
  cardLast4?: string;
  subtotal: number | null;
  tax: number | null;
  total: number | null;
  memo: string;
  /** Each item or charge, as printed (no tax): what it was, its amount, the best-fit category. */
  lines?: { description: string; amount: number; category: string }[];
  /** Payment terms as printed: "Net 30", "Due on receipt", "2% 10th Net 11th". */
  terms?: string;
  /** What the receipt says about where the work is: a ship-to or job address, job name or number, PO. */
  job?: { address?: string; name?: string; number?: string; po?: string };
  /** An early-payment discount offered ("You may deduct 6.80 if paid by 11/30/26"): dollars, and the last day. */
  earlyPayDiscount?: { amount?: number; by?: string };
  /** A credit memo: the invoice it credits, as printed. */
  originalInvoice?: string;
}

export interface ReceiptFields {
  vendorName: string;
  invoiceNumber: string;
  date: string | null;
  dueDate: string | null;
  kind: ReceiptKind;
  category: string;
  paymentMethod: string;
  /** Where a paid expense came out of: a QuickBooks bank or card account (null: the default). */
  paidFromAccountId: string | null;
  cardLast4: string;
  subtotalCents: number | null;
  taxCents: number | null;
  totalCents: number | null;
  memo: string;
  lines: ReceiptLine[];
  /** A bill's payment terms. */
  terms: string;
  /** Why a line has no job (overhead). */
  noJobReason: string;
  /** Why a cost goes on a job not in QuickBooks yet (approving creates it there). */
  earlyQbReason: string;
  /** A bill's early-payment discount: how much, and the last day to pay to get it. */
  earlyDiscountCents: number | null;
  earlyDiscountBy: string | null;
  /** Take it: approving also makes a vendor credit for it, to the discounts account (Settings → QuickBooks). */
  takeDiscount: boolean;
}

/** An account paid expenses come out of (Settings → QuickBooks), and the card's last 4 digits. */
export interface PaymentAccount {
  accountId: string;
  name: string;
  type: "bank" | "card";
  last4: string;
}
export const paymentAccountsSchema = z
  .array(z.object({ accountId: z.string().regex(/^\d{1,20}$/), name: z.string().trim().min(1).max(200), type: z.enum(["bank", "card"]), last4: z.string().regex(/^(\d{4})?$/, "The last 4 digits, or leave it empty") }))
  .max(30);

/**
 * Which account a paid expense came out of, from what was read off the receipt: the card whose last
 * 4 digits match; else, for a card payment with only one card on file, that card; else (cash, a
 * check, or unknown) the default account. Personal payments (to reimburse) came out of no company
 * account. Null means the default.
 */
export function pickPaidFrom(f: { kind: ReceiptKind; paymentMethod: string; cardLast4: string }, accounts: PaymentAccount[], defaultId: string | null): string | null {
  if (f.kind !== "expense" || f.paymentMethod === "Personal (reimburse)") return null;
  if (f.cardLast4) {
    const card = accounts.find((a) => a.last4 && a.last4 === f.cardLast4);
    if (card) return card.accountId;
  }
  if (f.paymentMethod === "Company card" || f.cardLast4) {
    const cards = accounts.filter((a) => a.type === "card");
    if (cards.length === 1) return cards[0]!.accountId;
    return defaultId && cards.some((c) => c.accountId === defaultId) ? defaultId : null;
  }
  return defaultId;
}

const MAX_CENTS = 100_000_000; // $1,000,000

/** Dollars as read (a number, or "1,234.50", "$12") → cents; null when it isn't an amount. */
export function receiptCents(v: unknown): number | null {
  const n = signedCents(v);
  return n === null || n < 0 ? null : n;
}

/** Like receiptCents, but a minus or parentheses ("(4.72)", "-4.72") is kept: a discount or credit. */
export function signedCents(v: unknown): number | null {
  const s = typeof v === "string" ? v.replace(/[$,\s]/g, "") : null;
  const paren = s !== null && /^\(.*\)$/.test(s);
  const n = typeof v === "number" ? v : s !== null ? Number(paren ? s.slice(1, -1) : s) * (paren ? -1 : 1) : Number.NaN;
  if (!Number.isFinite(n)) return null;
  const cents = Math.round(n * 100);
  return Math.abs(cents) <= MAX_CENTS ? cents : null;
}

const isoDate = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) ? v : null);

/**
 * The AI's reading → fields, with categories kept only when they're ours. Lines: each one read
 * (all on `jobId`), then the tax as its own line; with no lines read, one line for the amount before
 * tax, in the receipt's category.
 */
export function fieldsFromReading(r: Partial<ReceiptReading>, categories: readonly string[], jobId: string | null = null): ReceiptFields {
  const ours = (c: string | undefined) => categories.find((x) => x.toLowerCase() === (c ?? "").toLowerCase()) ?? "";
  const category = ours(r.category);
  const credit = r.kind === "credit";
  // A credit memo prints its amounts negative or in parentheses: what matters is how much.
  const abs = (v: unknown) => {
    const c = signedCents(v);
    return c === null ? null : Math.abs(c);
  };
  const total = abs(r.total);
  const subtotal = abs(r.subtotal);
  const tax = abs(r.tax);
  const totalCents = total ?? (subtotal !== null ? subtotal + (tax ?? 0) : null);
  const memo = [(r.memo ?? "").trim(), credit && r.originalInvoice?.trim() ? `Credit against invoice ${r.originalInvoice.trim()}` : ""].filter(Boolean).join(" · ").slice(0, 1000);
  // Each line as printed; a discount or coupon is a negative line (a credit's lines are all negative).
  const read = (Array.isArray(r.lines) ? r.lines : [])
    .map((l) => {
      const c = signedCents(l?.amount);
      return { category: ours(l?.category) || category, description: String(l?.description ?? "").trim().slice(0, 300), amountCents: c === null ? null : credit ? -Math.abs(c) : c, jobId };
    })
    .filter((l): l is ReceiptLine => l.amountCents !== null && l.amountCents !== 0)
    .slice(0, 100);
  const sign = credit ? -1 : 1;
  const lines: ReceiptLine[] = read.length ? read : totalCents ? [{ category, description: memo.slice(0, 300), amountCents: sign * (subtotal ?? totalCents - (tax ?? 0)), jobId }] : [];
  // A discount the reader missed: the items come to more than the printed subtotal (bills and expenses).
  const items = linesTotal(lines);
  if (!credit && read.length && subtotal !== null && items > subtotal && !read.some((l) => l.amountCents < 0)) {
    lines.push({ category: lines[0]!.category, description: "Discount (to match the printed subtotal)", amountCents: subtotal - items, jobId });
  }
  if (tax) lines.push({ category: ours(SALES_TAX) || SALES_TAX, description: "Sales tax", amountCents: sign * tax, jobId });
  const discount = !credit ? receiptCents(r.earlyPayDiscount?.amount) : null;
  const discountBy = discount ? isoDate(r.earlyPayDiscount?.by) : null;
  return {
    vendorName: (r.vendorName ?? "").trim().slice(0, 200),
    invoiceNumber: (r.invoiceNumber ?? "").trim().slice(0, 60),
    date: isoDate(r.date),
    dueDate: isoDate(r.dueDate),
    kind: credit ? "credit" : r.kind === "bill" ? "bill" : "expense",
    category,
    paymentMethod: PAYMENT_METHODS.find((m) => m === r.paymentMethod) ?? "",
    paidFromAccountId: null,
    cardLast4: /^\d{4}$/.test(String(r.cardLast4 ?? "").trim()) ? String(r.cardLast4).trim() : "",
    subtotalCents: subtotal,
    taxCents: tax,
    // Only a subtotal and tax read: their sum is the total.
    totalCents,
    memo,
    lines,
    terms: (r.terms ?? "").trim().slice(0, 60),
    noJobReason: "",
    earlyQbReason: "",
    earlyDiscountCents: discount && discountBy ? discount : null,
    earlyDiscountBy: discount ? discountBy : null,
    takeDiscount: true,
  };
}

/**
 * Lines for QuickBooks, which takes no negative lines on a bill, expense, or vendor credit: each
 * discount (a negative line) comes off the lines it most likely belongs to, first the same job and
 * category, then the same job, then any; largest first. A credit's lines (all negative) go as positive.
 */
export function netLines<L extends Pick<ReceiptLine, "amountCents" | "category" | "jobId">>(lines: readonly L[]): L[] {
  if (lines.length && lines.every((l) => l.amountCents <= 0)) return lines.map((l) => ({ ...l, amountCents: -l.amountCents })).filter((l) => l.amountCents > 0);
  const out = lines.filter((l) => l.amountCents > 0).map((l) => ({ ...l }));
  for (const d of lines.filter((l) => l.amountCents < 0)) {
    let left = -d.amountCents;
    const tiers = [out.filter((l) => l.jobId === d.jobId && l.category === d.category), out.filter((l) => l.jobId === d.jobId), out];
    for (const tier of tiers) {
      for (const l of [...tier].sort((a, b) => b.amountCents - a.amountCents)) {
        const take = Math.min(left, l.amountCents);
        l.amountCents -= take;
        left -= take;
        if (!left) break;
      }
      if (!left) break;
    }
  }
  return out.filter((l) => l.amountCents > 0);
}

/**
 * Lines as they go to QuickBooks, discounts kept as their own negative lines (QuickBooks takes them
 * when the total isn't negative): a credit's lines (stored negative) turn positive, $0 lines drop.
 * Null when that can't work (a negative total); then netLines.
 */
export function qbLines<L extends Pick<ReceiptLine, "amountCents">>(lines: readonly L[]): L[] | null {
  const sign = linesTotal(lines) < 0 ? -1 : 1;
  const out = lines.map((l) => ({ ...l, amountCents: l.amountCents * sign })).filter((l) => l.amountCents !== 0);
  return out.length && linesTotal(out) >= 0 ? out : null;
}

/** What the lines add up to. */
export const linesTotal = (lines: readonly Pick<ReceiptLine, "amountCents">[]) => lines.reduce((s, l) => s + l.amountCents, 0);

/** Subtotal and tax from the lines (tax = the Sales tax lines), and the main category: the largest line's. */
export function fromLines(lines: readonly ReceiptLine[]) {
  const taxCents = linesTotal(lines.filter((l) => l.category === SALES_TAX));
  const main = [...lines].filter((l) => l.category !== SALES_TAX).sort((a, b) => b.amountCents - a.amountCents)[0];
  // A credit's lines are negative; its subtotal and tax are kept as amounts.
  return { subtotalCents: Math.abs(linesTotal(lines) - taxCents), taxCents: Math.abs(taxCents), category: main?.category ?? lines[0]?.category ?? "" };
}

/**
 * The due date from payment terms and the bill date: "Net 30" → 30 days later; "Due on receipt" →
 * that day; "Net 10th" (or "2% 10th Net 11th") → that day of the next month. Null when the terms
 * don't say.
 */
export function dueFromTerms(terms: string, date: string): string | null {
  const t = terms.toLowerCase().replace(/\s+/g, " ").trim();
  if (!t || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const d = new Date(`${date}T00:00:00Z`);
  const iso = (x: Date) => x.toISOString().slice(0, 10);
  if (/due (on|upon) receipt|^cod$|^c\.o\.d\.?$|^due now$/.test(t)) return date;
  const ofMonth = /net (\d{1,2})(st|nd|rd|th)\b/.exec(t);
  if (ofMonth) {
    const day = Number(ofMonth[1]);
    if (day < 1 || day > 31) return null;
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() + 1;
    const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    return iso(new Date(Date.UTC(y, m, Math.min(day, last))));
  }
  const net = /net ?(\d{1,3})\b/.exec(t) ?? /^(\d{1,3}) days?$/.exec(t);
  if (net) return iso(new Date(d.getTime() + Number(net[1]) * 86_400_000));
  return null;
}

/** How far the lines are from the total (positive: the lines are short). Null without a total. A credit's lines are negative. */
export function linesOff(f: Pick<ReceiptFields, "lines" | "totalCents"> & { kind?: ReceiptKind }): number | null {
  return f.totalCents === null ? null : f.totalCents - (f.kind === "credit" ? -1 : 1) * linesTotal(f.lines);
}

/** Things to look at before marking it ready. */
export function receiptWarnings(f: Pick<ReceiptFields, "lines" | "totalCents" | "date" | "dueDate" | "kind">, today: string): string[] {
  const out: string[] = [];
  const off = linesOff(f);
  if (off) out.push(`The lines add up to ${usd(Math.abs(linesTotal(f.lines)))}, ${usd(Math.abs(off))} ${off > 0 ? "less" : "more"} than the total.`);
  if (f.date && f.date > today) out.push("The date is in the future.");
  if (f.kind === "bill" && f.dueDate && f.date && f.dueDate < f.date) out.push("The due date is before the bill date.");
  return out;
}

/**
 * What's missing before it can be marked ready. A bill needs a vendor from contacts (QuickBooks
 * does); an expense needs the account it was paid from (none by default for a personal payment); and
 * every line needs a job, or the receipt a reason it has none.
 */
export function receiptProblems(
  f: Pick<ReceiptFields, "vendorName" | "totalCents" | "date" | "lines"> & Partial<Pick<ReceiptFields, "kind" | "paymentMethod" | "paidFromAccountId" | "noJobReason" | "earlyQbReason">> & { vendorContactId?: string | null },
  ctx: { defaultAccountId?: string | null; jobsNotInQuickBooks?: number } = {},
): string[] {
  const out: string[] = [];
  // An expense needn't have a vendor (QuickBooks doesn't ask for one).
  if ((f.kind === "bill" || f.kind === "credit") && !f.vendorContactId) out.push("the vendor (pick them from contacts)");
  if (f.kind === "expense" && !(f.paidFromAccountId ?? (f.paymentMethod === "Personal (reimburse)" ? null : ctx.defaultAccountId))) out.push("the account it was paid from");
  if (!f.totalCents) out.push("the total");
  if (!f.date) out.push("the date");
  if (!f.lines.length) out.push("at least one line");
  else if (f.lines.some((l) => !l.category)) out.push("a category on every line");
  if (f.totalCents && linesOff(f)) out.push("lines that add up to the total");
  if (f.lines.some((l) => !l.jobId) && !(f.noJobReason ?? "").trim()) out.push("a job on every line, or why there isn't one");
  if (ctx.jobsNotInQuickBooks && !(f.earlyQbReason ?? "").trim()) out.push("why this unsold job needs to be in QuickBooks now");
  return out;
}

const usd = (cents: number) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── AI ───────────────────────────────────────────────────────────────────

export const RECEIPT_TOOL_NAME = "record_receipt";

export function receiptToolSchema(categories: readonly string[]) {
  return {
    type: "object",
    properties: {
      vendorName: { type: "string", description: "Who it's from: the store, supplier, or subcontractor's business name as printed." },
      invoiceNumber: { type: "string", description: "Invoice, receipt, or order number, if printed. Empty if none." },
      date: { type: "string", description: "Date of the purchase or invoice, YYYY-MM-DD. Empty if not printed." },
      dueDate: { type: "string", description: "Payment due date for an invoice, YYYY-MM-DD. Empty if none." },
      kind: { type: "string", enum: ["bill", "expense", "credit"], description: "bill = an invoice to be paid later (has terms, a due date, or 'amount due'); expense = already paid (a store receipt, a card charge); credit = money back from the vendor: a credit memo, a return, an RMA, or a total that's negative or in parentheses." },
      category: { type: "string", enum: [...categories], description: "The best fit for what was bought." },
      paymentMethod: { type: "string", enum: ["", "Company card", "Cash", "Check", "Personal (reimburse)", "Other"], description: "How it was paid, if the receipt shows it (card → Company card). Empty if unknown or a bill." },
      cardLast4: { type: "string", description: "If paid by card and the receipt prints the card number (often ****1234 or 'ending in 1234'), its last 4 digits. Empty otherwise." },
      subtotal: { type: "number", description: "Subtotal before tax, in dollars. Omit if not printed." },
      tax: { type: "number", description: "Sales tax in dollars. Omit if not printed." },
      total: { type: "number", description: "The grand total (or amount due) in dollars. For a credit, how much is credited, as a positive number." },
      memo: { type: "string", description: "A short description of what was bought, e.g. '12 bundles Duration Driftwood, drip edge'. Under 200 characters." },
      lines: {
        type: "array",
        description: "Each item or charge line as printed (not tax; tax goes in tax), with its line amount in dollars and the best-fit category. Freight, delivery, and fees are lines too. Every discount, coupon, or markdown printed as its own line (e.g. 'DISCOUNT (% 15.00)' -4.72) is a line with a NEGATIVE amount, right after the item it applies to, so the lines add up to the printed subtotal. For a credit, each credited item as a positive amount. If nothing is itemized, one line for the amount before tax.",
        items: {
          type: "object",
          properties: { description: { type: "string" }, amount: { type: "number" }, category: { type: "string", enum: [...categories] } },
          required: ["description", "amount", "category"],
        },
      },
      terms: { type: "string", description: "Payment terms if printed (e.g. 'Net 30', 'Due on receipt', '2% 10th Net 11th'). Empty if none." },
      earlyPayDiscount: {
        type: "object",
        description: "An early-payment discount the vendor offers on this invoice, only if printed (e.g. 'You may deduct 6.80 if paid by 11/30/26', or '2% 10th' with the amount). Omit if none.",
        properties: { amount: { type: "number", description: "Dollars that may be deducted." }, by: { type: "string", description: "The last day to pay to get it, YYYY-MM-DD." } },
      },
      originalInvoice: { type: "string", description: "For a credit: the original invoice it credits, if printed (e.g. 'Orig. Inv. Number'). Empty otherwise." },
      job: {
        type: "object",
        description: "Where the work is, if the receipt says: a ship-to or job address (not the store's or the bill-to address), a job name, a job number (only when it's labeled as one), or a PO / job name field exactly as printed (often a customer's last name or part of an address, e.g. SMITH or 1412 SW 4TH).",
        properties: { address: { type: "string" }, name: { type: "string" }, number: { type: "string" }, po: { type: "string" } },
      },
    },
    required: ["vendorName", "kind", "category", "total"],
  };
}

export const RECEIPT_PROMPT =
  "This is a receipt, invoice, bill, or credit memo for a roofing and construction company (NMT Construction, Oklahoma). Read it and call record_receipt with what it says. Use the amounts exactly as printed; don't compute anything that isn't printed except when a total is missing. Discount lines are negative lines, so the lines add up to the printed subtotal. A credit memo or return is kind credit. If it's not a receipt or invoice at all, still call the tool with your best reading and say so in memo.";

// ── API shapes ───────────────────────────────────────────────────────────

export interface ReceiptItem extends ReceiptFields {
  id: string;
  status: ReceiptStatus;
  jobId: string | null;
  jobLabel: string | null;
  vendorContact: { id: string; name: string; category: string } | null;
  /** previewUrl: the browser-made JPEG copy of a photo when there is one (phones save HEIC). */
  file: { id: string; name: string; url: string; contentType: string; previewUrl: string } | null;
  readError: string | null;
  warnings: string[];
  complianceOverride: { reason: string; problems: string[]; by: string; at: string } | null;
  submittedBy: UserRef | null;
  submittedAt: string;
  readyAt: string | null;
  approvedBy: UserRef | null;
  approvedAt: string | null;
  /** In QuickBooks (Bill or Purchase), and when a Bill was paid there. */
  inQuickBooks: boolean;
  qbPaidAt: string | null;
  /** "quickbooks": entered in QuickBooks and read from there; it can't be changed here. */
  source: "crm" | "quickbooks";
  /** Void: deleted in QuickBooks. Counts nowhere; shown on its job only. */
  voided: { at: string; reason: string } | null;
  /** A discount taken that wasn't paid in time (its vendor credit was deleted in QuickBooks). */
  discountLapsedAt: string | null;
  /** Split from a file holding several invoices: that file and which of its pages. */
  splitFrom: { name: string; url: string; pages: string } | null;
  /** Bill, Purchase, or VendorCredit, once it's in QuickBooks. */
  qbType: string | null;
  /** The work order this bill was drafted from. */
  workOrder: { id: string; number: number } | null;
  /** Jobs on the lines that aren't in QuickBooks yet (approving creates them, with a reason). */
  jobsNotInQuickBooks: { id: string; label: string }[];
  /** "#1001 · Pat Smith" for each job on a line. */
  jobLabels: Record<string, string>;
  /** The vendor's payment terms (vendor and sub contacts), for bills. */
  vendorTerms: string;
  /** A sub's bill on a job: their work orders there, to compare amounts side by side. */
  matchingWorkOrders: { id: string; number: number; title: string; totalCents: number; status: string }[];
}

const dateField = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable();
const cents = z.number().int().min(0).max(MAX_CENTS).nullable();

export const receiptCreateSchema = z.object({ fileId: z.string().uuid() });

export const receiptUpdateSchema = z.object({
  vendorName: z.string().trim().max(200).optional(),
  vendorContactId: z.string().uuid().nullable().optional(),
  invoiceNumber: z.string().trim().max(60).optional(),
  date: dateField.optional(),
  dueDate: dateField.optional(),
  kind: z.enum(["bill", "expense", "credit"]).optional(),
  category: z.string().trim().max(60).optional(),
  paymentMethod: z.string().trim().max(40).optional(),
  paidFromAccountId: z.string().regex(/^\d{1,20}$/).nullable().optional(),
  cardLast4: z.string().regex(/^(\d{4})?$/).optional(),
  subtotalCents: cents.optional(),
  taxCents: cents.optional(),
  totalCents: cents.optional(),
  memo: z.string().trim().max(1000).optional(),
  jobId: z.string().uuid().nullable().optional(),
  lines: z.array(z.object({ category: z.string().trim().max(60), description: z.string().trim().max(4000), amountCents: z.number().int().min(-MAX_CENTS).max(MAX_CENTS), jobId: z.string().uuid().nullable() })).max(100).optional(),
  terms: z.string().trim().max(60).optional(),
  noJobReason: z.string().trim().max(300).optional(),
  earlyQbReason: z.string().trim().max(300).optional(),
  earlyDiscountCents: cents.optional(),
  earlyDiscountBy: dateField.optional(),
  takeDiscount: z.boolean().optional(),
});

/** Something this receipt may duplicate: another receipt, or a bill already in QuickBooks. */
export interface ReceiptMatch {
  kind: "receipt" | "quickbooks";
  id: string;
  label: string;
  date: string | null;
  totalCents: number | null;
  why: string;
}

export const receiptReadySchema = z.object({ overrideReason: z.string().trim().max(1000).optional() });

// ── One file, several invoices ───────────────────────────────────────────

/** An invoice the reader found in a file of several: its pages (from 1) and what identifies it. */
export interface FoundDocument {
  firstPage: number;
  lastPage: number;
  invoiceNumber: string;
  total: number | null;
}

export const DOCUMENTS_TOOL_NAME = "list_documents";

export const DOCUMENTS_PROMPT =
  "This PDF may hold more than one invoice, bill, receipt, or credit memo. List each one with the pages it's on (page 1 is the first). One invoice can run several pages: use 'Page 1 of 3', the invoice number repeated in each page's header, 'continued', and where the totals are printed (on an invoice's last page). A page that isn't an invoice (a cover sheet, terms, a blank page) belongs to the invoice next to it. Every page belongs to exactly one invoice, in order. If it's all one invoice, list just one.";

export function documentsToolSchema() {
  return {
    type: "object",
    properties: {
      documents: {
        type: "array",
        items: {
          type: "object",
          properties: {
            firstPage: { type: "integer", description: "Its first page, from 1." },
            lastPage: { type: "integer", description: "Its last page." },
            invoiceNumber: { type: "string", description: "Its invoice, receipt, or credit memo number, as printed. Empty if none." },
            total: { type: "number", description: "Its total in dollars as printed (a credit as a positive number)." },
          },
          required: ["firstPage", "lastPage", "invoiceNumber"],
        },
      },
    },
    required: ["documents"],
  };
}

/**
 * The page ranges to split a file into, when the reader found several invoices and its pages make
 * sense: in order, every page in exactly one, each with a total. Null otherwise (keep it whole).
 */
export function planSplit(docs: readonly Partial<FoundDocument>[], pageCount: number): { from: number; to: number; invoiceNumber: string }[] | null {
  if (docs.length < 2 || pageCount < 2) return null;
  const out: { from: number; to: number; invoiceNumber: string }[] = [];
  let next = 1;
  for (const d of [...docs].sort((a, b) => (a.firstPage ?? 0) - (b.firstPage ?? 0))) {
    const from = Number(d.firstPage);
    const to = Number(d.lastPage);
    if (!Number.isInteger(from) || !Number.isInteger(to) || from !== next || to < from || to > pageCount) return null;
    if (!(typeof d.total === "number" && d.total !== 0)) return null;
    out.push({ from, to, invoiceNumber: String(d.invoiceNumber ?? "").trim().slice(0, 60) });
    next = to + 1;
  }
  return next === pageCount + 1 ? out : null;
}

/** "1-3, 4" → [{from:1,to:3},{from:4,to:4}] for a split by hand; null when it isn't every page once, in order. */
export function parseRanges(text: string, pageCount: number): { from: number; to: number }[] | null {
  const parts = text.split(/[,;\s]+/).filter(Boolean);
  const out: { from: number; to: number }[] = [];
  let next = 1;
  for (const p of parts) {
    const m = /^(\d+)(?:-(\d+))?$/.exec(p);
    if (!m) return null;
    const from = Number(m[1]);
    const to = Number(m[2] ?? m[1]);
    if (from !== next || to < from || to > pageCount) return null;
    out.push({ from, to });
    next = to + 1;
  }
  return out.length >= 2 && next === pageCount + 1 ? out : null;
}

export const receiptSplitSchema = z.object({ ranges: z.string().trim().min(1).max(500) });

/** Who a receipt with no vendor could be from (receipt page). */
export interface VendorSuggestions {
  contacts: { id: string; name: string }[];
  /** A QuickBooks vendor by the printed name, not in contacts yet: one tap brings it in. */
  quickbooks: { qbId: string; name: string } | null;
}
